import { useCallback, useState, createContext, useContext, useMemo } from 'react';
import { ContextData, ContextProviderProps } from 'types/context';
import { ListVariables } from '../api/variables-endpoint';
import { VariableIdentifier } from '../types/variable';

const VariablesContext = createContext<ContextData<VariableIdentifier> | undefined>(undefined);
VariablesContext.displayName = 'VariablesContext';

function useVariableContextData(): ContextData<VariableIdentifier> {
  const [variables, setVariables] = useState<VariableIdentifier[]>([]);
  const [filteredVariables, setFilteredVariables] = useState<VariableIdentifier[] | undefined>(undefined);
  const [isLoading, setIsLoading] = useState(false);

  const fetchVariables = useCallback(async () => {
    setIsLoading(true);
    const listVariablesResponse = await ListVariables();
    if (listVariablesResponse.success && listVariablesResponse.data) {
      setVariables(listVariablesResponse.data);
    } else {
      setVariables([]);
    }
    setIsLoading(false);
  }, [setVariables]);

  const filterVariables = useCallback(
    async (filter: string) => {
      if (filter === '') {
        setFilteredVariables(undefined);
        return;
      }

      const filterResult = variables.filter((variable) => {
        return variable.Name.toLowerCase().includes(filter.toLowerCase()) || variable.Description?.toLowerCase().includes(filter.toLowerCase());
      });

      setFilteredVariables(filterResult);
    },
    [setFilteredVariables, variables],
  );

  return useMemo(
    () => ({
      data: filteredVariables ?? variables,
      isLoading,
      fetchData: fetchVariables,
      filterData: filterVariables,
    }),
    [variables, filteredVariables, isLoading, fetchVariables, filterVariables],
  );
}

export const useVariablesContext = (): ContextData<VariableIdentifier> => {
  const variablesContext = useContext(VariablesContext);
  if (!variablesContext) {
    throw new Error('useVariablesContext must be used within the VariablesContext.Provider');
  }
  return variablesContext;
};

export function VariablesContextProvider({ children }: ContextProviderProps): JSX.Element {
  const variableContextData = useVariableContextData();
  return <VariablesContext.Provider value={variableContextData}>{children}</VariablesContext.Provider>;
}

export default { useVariablesContext, VariablesContextProvider };
